import { MaturityScheduleResponse } from '../../model/MaturitySchedule.model';

// Status color mapping
export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'PENDING':
      return 'orange';
    case 'OVERDUE':
      return 'red';
    case 'PAID':
      return 'blue';
    case 'COMPLETED':
      return 'green';
    case 'ABANDONED':
      return 'gray';
    default:
      return 'default';
  }
};

// Status text mapping
export const getStatusText = (status: string): string => {
  switch (status) {
    case 'PENDING':
      return 'Đang chờ';
    case 'OVERDUE':
      return 'Quá hạn';
    case 'PAID':
      return 'Đã nạp đáo';
    case 'COMPLETED':
      return 'Đã hoàn thành';
    case 'ABANDONED':
      return 'Không làm';
    default:
      return status;
  }
};

// Currency formatting
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('vi-VN', {
    style: 'currency',
    currency: 'VND',
  }).format(amount);
};

// Card number formatting
export const formatCardNumber = (cardNumber: string): string => {
  if (!cardNumber) return 'N/A';
  // Lấy 4 số cuối và thêm *** phía trước
  const lastFour = cardNumber.slice(-4);
  return `***${lastFour}`;
};

// Transaction calculation helpers
export const getTotalDepositAmount = (
  record: MaturityScheduleResponse
): number => {
  if (record.totalDepositAmount !== undefined) {
    return record.totalDepositAmount;
  }
  // Fallback: tính từ transactions nếu có (chỉ tính các giao dịch chưa bị xóa)
  return (
    record.transactions?.reduce((total, transaction) => {
      if (
        transaction.transactionType === 'deposit' &&
        transaction.status === 'COMPLETED' &&
        !transaction.deletedAt // Loại bỏ giao dịch đã bị soft delete
      ) {
        return total + (transaction.amount || 0);
      }
      return total;
    }, 0) || 0
  );
};

export const getTotalWithdrawalAmount = (
  record: MaturityScheduleResponse
): number => {
  if (record.totalWithdrawalAmount !== undefined) {
    return record.totalWithdrawalAmount;
  }
  // Fallback: tính từ transactions nếu có (chỉ tính các giao dịch chưa bị xóa)
  return (
    record.transactions?.reduce((total, transaction) => {
      if (
        transaction.transactionType === 'withdrawal' &&
        !transaction.deletedAt // Loại bỏ giao dịch đã bị soft delete
      ) {
        return total + (transaction.amount || 0);
      }
      return total;
    }, 0) || 0
  );
};

export const getRemainingAmount = (
  record: MaturityScheduleResponse
): number => {
  if (record.remainingAmount !== undefined) {
    return record.remainingAmount;
  }
  // Fallback: tính từ repaymentAmount - totalDeposit
  const totalDeposit = getTotalDepositAmount(record);
  return record.repaymentAmount - totalDeposit;
};

export const getBalanceDifference = (
  record: MaturityScheduleResponse
): number => {
  if (record.balanceDifference !== undefined) {
    return record.balanceDifference;
  }
  // Fallback: tính từ totalDeposit - totalWithdrawal
  const totalDeposit = getTotalDepositAmount(record);
  const totalWithdrawal = getTotalWithdrawalAmount(record);
  return totalDeposit - totalWithdrawal;
};

/**
 * Tính tổng thành tiền theo công thức: phí thu * rút + chênh lệch
 * @param record - MaturityScheduleResponse object
 * @returns Tổng thành tiền
 */
export const getTotalAmount = (record: MaturityScheduleResponse): number => {
  const feeRate = record.feeApplied || 0; // Phí thu (%)
  const totalWithdrawal = getTotalWithdrawalAmount(record); // Tổng rút
  const balanceDifference = getBalanceDifference(record); // Chênh lệch

  // Tính phí thu theo số tiền rút
  const feeAmount = (totalWithdrawal * feeRate) / 100;

  // Thành tiền = phí thu + chênh lệch
  return feeAmount + balanceDifference;
};
